import { useState } from 'react'

import { F0AvatarList, F0Box, F0Dialog, F0Text } from '@factorialco/f0-react'
import {
  DetailsItemsList,
  DetailsItemType,
  ResourceHeader,
  RichTextDisplay,
  Textarea,
} from '@factorialco/f0-react/dist/experimental'
import { Check, Cross } from '@factorialco/f0-react/icons/app'
import {
  ApproveTrainingRequestDatatableDocument,
  TrainingRequestActivityDocument,
  TrainingRequestsDataCollectionDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { isAbsoluteURL } from 'modules/core/lib/isAbsoluteURL'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { useRejectTrainingRequest } from 'modules/trainings/hooks/TrainingRequests/useRejectTrainingRequest'
import {
  getStatusColor,
  getStatusLabel,
} from 'modules/trainings/components/Requests/RequestsDataCollection/statusUtils'

type Data = {
  id: string
}

const getScheduleLabel = (schedule: string): string => {
  switch (schedule) {
    case 'workinghours':
      return i18n.t('trainings.training_requests.create.schedule_options.during_work_hours')
    case 'mixedhours':
      return i18n.t('trainings.training_requests.create.schedule_options.mixed_working_hours')
    case 'notsure':
      return i18n.t('trainings.training_requests.create.schedule_options.not_sure')
    case 'outsideworkinghours':
      return i18n.t('trainings.training_requests.create.schedule_options.outside_working_hours')
    default:
      return schedule
  }
}

export const TrainingRequestActivity: ActivityComponent<Data> = ({ id }) => {
  const { open, closeActivity } = useActivity()
  const {
    company: { id: companyId },
    access: { id: currentAccessId },
  } = useCurrentSession()

  const { data, loading } = useQuery(TrainingRequestActivityDocument, {
    variables: {
      id,
    },
    skip: !open,
  })

  const [approve] = useMutation(ApproveTrainingRequestDatatableDocument, {
    extractorFn: (result) => result.trainingsMutations.approveTrainingsTrainingRequest,
    refetchQueries: [TrainingRequestsDataCollectionDocument],
    awaitRefetchQueries: true,
  })

  const { reject, loading: rejecting } = useRejectTrainingRequest()
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const request = data?.trainings?.trainingRequest

  if (!open) return null

  const showLoader = !request && loading

  const isApprover = request?.currentApproverIds?.includes(currentAccessId) ?? false
  const showApproveOptions = request?.status === 'review' && isApprover

  const isFromCatalog = request?.trainingId != null

  const details = request
    ? [
        {
          title: i18n.t('trainings.training_requests.my_requests.detail.training_type.title'),
          content: {
            type: 'dot-tag' as const,
            text: isFromCatalog
              ? i18n.t('trainings.training_requests.create.button.from_catalog')
              : i18n.t('trainings.training_requests.create.button.new'),
            color: 'smoke' as const,
          },
        },
        {
          title: i18n.t('trainings.training_requests.requests.datatable.requested_by'),
          content: {
            type: 'person' as const,
            firstName: request.author?.fullName ?? '',
            lastName: '',
            avatarUrl: request.author?.avatar ? buildUrl(request.author.avatar) : undefined,
          },
        },
      ]
    : []

  const participants = request?.participantsConnection?.nodes ?? []
  const participantsTotalCount = request?.participantsConnection?.totalCount ?? 0
  const participantsRemainingCount = Math.max(participantsTotalCount - participants.length, 0)

  const competencies = request?.competencies ?? []

  // Basic information details for new proposals
  const basicInfoDetails: DetailsItemType[] =
    !isFromCatalog && request
      ? [
          ...(request.providerName
            ? [
                {
                  title: i18n.t('trainings.training_requests.my_requests.detail.provider_name'),
                  content: {
                    type: 'item' as const,
                    text: request.providerName,
                  },
                },
              ]
            : []),
          ...(request.externalReference
            ? [
                {
                  title: i18n.t('trainings.training_requests.my_requests.detail.link'),
                  content: {
                    type: 'item' as const,
                    text: request.externalReference,
                    ...(isAbsoluteURL(request.externalReference) && {
                      action: {
                        type: 'open-link' as const,
                        href: request.externalReference,
                      },
                    }),
                  },
                },
              ]
            : []),
          ...(competencies.length > 0
            ? [
                {
                  title: i18n.t(
                    'trainings.training_requests.my_requests.detail.selector_competencies'
                  ),
                  content: [
                    ...competencies.slice(0, 2).map((c) => ({
                      type: 'dot-tag' as const,
                      text: c.name,
                      color: 'smoke' as const,
                    })),
                    ...(competencies.length > 2
                      ? [
                          {
                            type: 'item' as const,
                            text: `+${competencies.length - 2}`,
                          },
                        ]
                      : []),
                  ],
                },
              ]
            : []),
        ]
      : []

  // Timings and cost details for new proposals
  const timingsAndCostDetails: DetailsItemType[] =
    !isFromCatalog && request
      ? [
          ...(request.startDate
            ? [
                {
                  title: i18n.t('trainings.training_requests.my_requests.detail.start_date'),
                  content: {
                    type: 'item' as const,
                    text: dateBackendDateTimeToUI(request.startDate),
                  },
                },
              ]
            : []),
          ...(request.duration
            ? [
                {
                  title: i18n.t(
                    'trainings.training_requests.my_requests.detail.total_training_hours'
                  ),
                  content: {
                    type: 'item' as const,
                    text: request.duration + 'h',
                  },
                },
              ]
            : []),
          ...(request.schedule
            ? [
                {
                  title: i18n.t('trainings.training_requests.my_requests.detail.training_schedule'),
                  content: {
                    type: 'dot-tag' as const,
                    text: getScheduleLabel(request.schedule),
                    color: 'smoke' as const,
                  },
                },
              ]
            : []),
          ...(request.cost
            ? [
                {
                  title: i18n.t('trainings.training_requests.my_requests.detail.cost'),
                  content: {
                    type: 'item' as const,
                    text: new Intl.NumberFormat(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(request.cost)),
                  },
                },
              ]
            : []),
          {
            title: i18n.t('trainings.training_requests.my_requests.detail.subsidized_cost'),
            content: {
              type: 'item' as const,
              text: request.subsidized ? i18n.t('common.yes') : i18n.t('common.no'),
            },
          },
        ]
      : []

  const handleApprove = async () => {
    if (!request) return

    const result = await approve({
      variables: { id: request.id, companyId },
    })

    const handled = handleResult(result, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('trainings.training_requests.create.wrong') })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.training_requests.create.success_messages.approved'),
      })
      closeActivity()
    }
  }

  const handleReject = () => {
    setShowRejectDialog(true)
  }

  const confirmReject = async () => {
    if (!request) return

    const result = await reject({
      id: request.id,
      companyId,
      reason: rejectReason,
    })

    const handled = handleResult(result, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('trainings.training_requests.create.wrong') })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.training_requests.create.success_messages.rejected'),
      })
      setShowRejectDialog(false)
      setRejectReason('')
      closeActivity()
    }
  }

  return (
    <>
      <F0Dialog
        title={i18n.t('trainings.training_requests.my_requests.detail.title')}
        isOpen={open}
        onClose={closeActivity}
        position="right"
        primaryAction={
          showApproveOptions
            ? {
                label: i18n.t('common.approve'),
                icon: Check,
                onClick: handleApprove,
              }
            : undefined
        }
        secondaryAction={
          showApproveOptions
            ? {
                label: i18n.t('common.reject'),
                icon: Cross,
                onClick: handleReject,
              }
            : undefined
        }
        module={{ id: 'my_trainings', label: i18n.t('trainings.training_requests.header.title') }}
      >
        {request ? (
          <>
            <ResourceHeader
              title={request.trainingName ?? '-'}
              status={{
                label: i18n.t('trainings.training_requests.requests.datatable.status'),
                text: getStatusLabel(request.status),
                variant: getStatusColor(request.status),
              }}
              metadata={[
                {
                  label: i18n.t('trainings.training_requests.requests.datatable.created'),
                  value: {
                    type: 'text',
                    content: request.createdAt ? dateBackendDateTimeToUI(request.createdAt) : '-',
                  },
                },
              ]}
            />
            <F0Box display="flex" flexDirection="column" gap="xl" paddingTop="lg">
              {/* Details list: Status, Requester, Date, Type */}
              <DetailsItemsList details={details} tableView />

              {/* Participants */}
              {participantsTotalCount > 0 && (
                <F0Box display="flex" flexDirection="column" gap="lg">
                  <F0Text
                    color="secondary"
                    content={i18n.t(
                      'tasks.approvals.trainings_training_request.fields.participants'
                    )}
                  />
                  <F0AvatarList
                    avatars={participants.flatMap((p) => {
                      if (!p) return []
                      return [
                        {
                          firstName: p.firstName ?? '',
                          lastName: p.lastName ?? '',
                          src: p.avatar?.url ? buildUrl(p.avatar.url) : undefined,
                        },
                      ]
                    })}
                    size="md"
                    type="person"
                    remainingCount={
                      participantsRemainingCount > 0 ? participantsRemainingCount : undefined
                    }
                  />
                </F0Box>
              )}

              {/* Course need (new proposal only) */}
              {!isFromCatalog && request.trainingNeed && (
                <F0Box display="flex" flexDirection="column" gap="lg">
                  <F0Text
                    color="secondary"
                    content={i18n.t('trainings.training_requests.my_requests.detail.training_need')}
                  />
                  <F0Box background="secondary" padding="md" borderRadius="md">
                    <RichTextDisplay content={request.trainingNeed} />
                  </F0Box>
                </F0Box>
              )}

              {/* Additional comments (new proposal only) */}
              {!isFromCatalog && request.comments && (
                <F0Box display="flex" flexDirection="column" gap="lg">
                  <F0Text
                    color="secondary"
                    content={i18n.t(
                      'trainings.training_requests.my_requests.detail.additional_information'
                    )}
                  />
                  <F0Box background="secondary" padding="md" borderRadius="md">
                    <RichTextDisplay content={request.comments} />
                  </F0Box>
                </F0Box>
              )}

              {/* Basic information (new proposal only) */}
              {!isFromCatalog && basicInfoDetails.length > 0 && (
                <F0Box display="flex" flexDirection="column" gap="md">
                  <F0Text
                    variant="label"
                    content={i18n.t(
                      'trainings.training_requests.my_requests.detail.basic_information'
                    )}
                  />
                  <DetailsItemsList details={basicInfoDetails} tableView />
                </F0Box>
              )}

              {/* Timings and cost (new proposal only) */}
              {!isFromCatalog && timingsAndCostDetails.length > 0 && (
                <F0Box display="flex" flexDirection="column" gap="md">
                  <F0Text
                    variant="label"
                    content={i18n.t(
                      'trainings.training_requests.my_requests.detail.timings_and_costs'
                    )}
                  />
                  <DetailsItemsList details={timingsAndCostDetails} tableView />
                </F0Box>
              )}

              {/* Approval history */}
              <ApprovalsInformationBlock
                resourceId={request.id}
                resourceType="Trainings::TrainingRequest"
                newLook
              />
            </F0Box>
          </>
        ) : (
          showLoader && <Loading />
        )}
      </F0Dialog>
      {showRejectDialog && (
        <F0Dialog
          isOpen
          onClose={() => {
            setShowRejectDialog(false)
            setRejectReason('')
          }}
          title={i18n.t('trainings.training_requests.my_requests.detail.reject_modal.title')}
          primaryAction={{
            label: i18n.t('trainings.training_requests.my_requests.detail.reject_modal.reject'),
            onClick: confirmReject,
            loading: rejecting,
            disabled: rejecting,
          }}
          secondaryAction={{
            label: i18n.t('trainings.training_requests.my_requests.detail.reject_modal.cancel'),
            onClick: () => {
              setShowRejectDialog(false)
              setRejectReason('')
            },
          }}
        >
          <Textarea
            label={i18n.t(
              'trainings.training_requests.my_requests.detail.reject_modal.comments_label'
            )}
            value={rejectReason}
            onChange={setRejectReason}
          />
        </F0Dialog>
      )}
    </>
  )
}
